"use client";

import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  TriangleAlert,
} from "lucide-react";
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FilterSelect,
  PageHeader,
  cn,
  useToast,
} from "@chotca/ui";
import {
  INTERVIEW_DAY_END_HOUR,
  INTERVIEW_DAY_START_HOUR,
  type InterviewCalendarItem,
} from "@chotca/config";
import {
  useEmployerInterviews,
  useMe,
  useOrgAddresses,
  useReportNoShow,
} from "@chotca/api-client";
import { WorkerProfileModal } from "@/components/worker-profile-modal";

const WD = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const HALF = 30 * 60 * 1000;

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const dow = out.getDay();
  out.setDate(out.getDate() + (dow === 0 ? -6 : 1 - dow));
  return out;
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function ddmm(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function InterviewsClient() {
  const t = useTranslations("interviews");
  const { toast } = useToast();
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  const addresses = useOrgAddresses(orgId ?? null);

  const [weekStart, setWeekStart] = React.useState(() =>
    startOfWeek(new Date()),
  );
  const [addressId, setAddressId] = React.useState("");
  const weekEnd = addDays(weekStart, 7);

  const { data, isLoading } = useEmployerInterviews({
    from: weekStart.toISOString(),
    to: weekEnd.toISOString(),
    orgAddressId: addressId || undefined,
  });
  const items = data?.items ?? [];
  const pendingCount = data?.pendingCount ?? 0;

  const noShow = useReportNoShow();
  const [selected, setSelected] = React.useState<InterviewCalendarItem | null>(
    null,
  );
  const [confirmNoShow, setConfirmNoShow] = React.useState(false);
  const [profileAppId, setProfileAppId] = React.useState<string | null>(null);

  const days = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Default day band (shared with the slot picker), auto-expanding if any
  // interview falls earlier/later so nothing is ever clipped.
  const { startHour, endHour } = React.useMemo(() => {
    let min = INTERVIEW_DAY_START_HOUR;
    let max = INTERVIEW_DAY_END_HOUR;
    for (const it of items) {
      const s = new Date(it.startAt);
      const e = new Date(it.endAt);
      min = Math.min(min, s.getHours());
      max = Math.max(max, e.getMinutes() > 0 ? e.getHours() + 1 : e.getHours());
    }
    return { startHour: Math.max(0, min), endHour: Math.min(24, max) };
  }, [items]);

  const rowCount = (endHour - startHour) * 2;
  const todayKey = new Date().toDateString();

  function dayIndexOf(isoStr: string): number {
    const d = new Date(isoStr);
    return Math.floor(
      (new Date(d.toDateString()).getTime() - weekStart.getTime()) / 86_400_000,
    );
  }

  const addressOptions = (addresses.data?.items ?? []).map((a) => ({
    value: a.id,
    label: a.alias,
  }));

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("title")}
        description={t("weekCount", { n: items.length })}
      />

      {/* week nav + location filter on one row */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setWeekStart((d) => addDays(d, -7))}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-smoke text-ash transition hover:bg-mist hover:text-ink"
          aria-label={t("prevWeek")}
        >
          <ChevronLeft size={15} />
        </button>
        <span className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-ink">
          {ddmm(days[0]!)} – {ddmm(days[6]!)}
        </span>
        <button
          type="button"
          onClick={() => setWeekStart((d) => addDays(d, 7))}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-smoke text-ash transition hover:bg-mist hover:text-ink"
          aria-label={t("nextWeek")}
        >
          <ChevronRight size={15} />
        </button>
        {addressOptions.length > 1 ? (
          <FilterSelect
            value={addressId}
            onValueChange={setAddressId}
            placeholder={t("allBranches")}
            options={addressOptions}
            width="w-auto"
            className="ml-auto h-8 max-w-37.5 border bg-paper px-2.5 text-[13px] font-medium"
          />
        ) : null}
      </div>

      {/* legend */}
      <div className="flex items-center gap-4 text-[11px] text-ash">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-lime-dark" />
          {t("confirmed")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-sun-dark" />
          {t("editable")}
        </span>
      </div>

      {/* Mobile: agenda grouped by day. Empty days collapse to a thin line so a
          busy week (30-50 interviews) stays scannable; each interview is one
          compact tappable row, sorted by time. */}
      <div className="divide-y divide-smoke overflow-hidden rounded-2xl border border-smoke bg-paper md:hidden">
        {days.map((d, i) => {
          const dayItems = items
            .filter((it) => dayIndexOf(it.startAt) === i)
            .sort((a, b) => +new Date(a.startAt) - +new Date(b.startAt));
          const isToday = d.toDateString() === todayKey;
          const empty = dayItems.length === 0;
          return (
            <div
              key={d.toISOString()}
              className={cn(isToday && "bg-lime/[0.07]")}
            >
              {/* day header */}
              <div
                className={cn(
                  "flex items-center gap-2.5 px-3.5",
                  empty ? "py-2" : "pb-1.5 pt-2.5",
                )}
              >
                <span className="w-7 text-[11px] font-semibold uppercase text-ash">
                  {WD[d.getDay() === 0 ? 6 : d.getDay() - 1]}
                </span>
                <span
                  className={cn(
                    "text-[15px] font-bold tabular-nums",
                    empty ? "text-ash" : "text-ink",
                  )}
                >
                  {String(d.getDate()).padStart(2, "0")}
                </span>
                {empty ? (
                  <span className="text-xs text-ash/50">—</span>
                ) : (
                  <span className="ml-auto rounded-full bg-mist px-2 py-0.5 text-[11px] font-semibold text-ash">
                    {t("dayCount", { n: dayItems.length })}
                  </span>
                )}
              </div>
              {/* interviews */}
              {dayItems.map((it) => (
                <button
                  key={it.inviteId}
                  type="button"
                  onClick={() => setSelected(it)}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 pl-13 text-left transition active:bg-mist/60"
                >
                  <span
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      it.committed ? "bg-lime-dark" : "bg-sun-dark",
                    )}
                  />
                  <span className="shrink-0 text-[13px] font-bold tabular-nums text-ink">
                    {hhmm(new Date(it.startAt))}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink/80">
                    {it.workerFullName ?? it.workerMaskedName}
                  </span>
                  <ChevronRight size={15} className="shrink-0 text-ash/60" />
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Desktop: full week time-grid (needs the width). */}
      <div className="hidden overflow-x-auto rounded-2xl border border-smoke bg-paper md:block">
        <div
          className="grid min-w-[680px]"
          style={{
            gridTemplateColumns: "52px repeat(7, minmax(84px, 1fr))",
            gridTemplateRows: `auto repeat(${rowCount}, 26px)`,
          }}
        >
          {/* header */}
          <div className="sticky left-0 z-10 border-b border-smoke bg-mist/60" />
          {days.map((d) => {
            const isToday = d.toDateString() === todayKey;
            return (
              <div
                key={d.toISOString()}
                className={cn(
                  "border-b border-l border-smoke py-2 text-center",
                  isToday ? "bg-lime/15" : "bg-mist/40",
                )}
              >
                <div className="text-[11px] font-semibold text-ash">
                  {WD[d.getDay() === 0 ? 6 : d.getDay() - 1]}
                </div>
                <div className="text-sm font-extrabold text-ink tabular-nums">
                  {String(d.getDate()).padStart(2, "0")}
                </div>
              </div>
            );
          })}

          {/* time labels + background cells */}
          {Array.from({ length: rowCount }, (_, r) => {
            const isHour = r % 2 === 0;
            const hour = startHour + Math.floor(r / 2);
            return (
              <React.Fragment key={`row-${r}`}>
                <div
                  className={cn(
                    "pr-1.5 text-right text-[10px] text-ash",
                    // First label sits at the top of its cell so it isn't
                    // pushed up under the sticky header; the rest align to the
                    // hour gridline.
                    r > 0 && "-translate-y-2",
                  )}
                  style={{ gridColumn: 1, gridRow: r + 2 }}
                >
                  {isHour ? `${hour}:00` : ""}
                </div>
                {days.map((d, di) => {
                  const isToday = d.toDateString() === todayKey;
                  return (
                    <div
                      key={`c-${r}-${di}`}
                      className={cn(
                        "border-l border-smoke",
                        isHour ? "border-t border-t-smoke/70" : "",
                        isToday ? "bg-lime/[0.06]" : "",
                      )}
                      style={{ gridColumn: di + 2, gridRow: r + 2 }}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* events */}
          {items.map((it) => {
            const di = dayIndexOf(it.startAt);
            if (di < 0 || di > 6) return null;
            const s = new Date(it.startAt);
            const e = new Date(it.endAt);
            const rowStart =
              (s.getHours() - startHour) * 2 + (s.getMinutes() >= 30 ? 1 : 0);
            if (rowStart < 0 || rowStart >= rowCount) return null;
            const span = Math.max(
              1,
              Math.round((e.getTime() - s.getTime()) / HALF),
            );
            return (
              <button
                key={it.inviteId}
                type="button"
                onClick={() => setSelected(it)}
                className={cn(
                  "z-[2] m-0.5 flex flex-col overflow-hidden rounded-lg border px-1.5 py-1 text-left transition active:scale-[0.98]",
                  it.committed
                    ? "border-lime bg-lime-light/70 text-lime-dark hover:bg-lime-light"
                    : "border-sun bg-sun-light text-sun-dark hover:brightness-95",
                )}
                style={{
                  gridColumn: di + 2,
                  gridRow: `${rowStart + 2} / span ${span}`,
                }}
              >
                <span className="text-[10px] font-extrabold leading-none tabular-nums">
                  {hhmm(s)}
                </span>
                <span className="truncate text-[11px] font-bold leading-tight text-ink">
                  {it.workerFullName ?? it.workerMaskedName}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!isLoading && items.length === 0 ? (
        <p className="py-6 text-center text-sm text-ash">{t("empty")}</p>
      ) : null}

      {/* pending invites banner */}
      {pendingCount > 0 ? (
        <Link
          to="/applicants"
          className="flex items-center gap-2.5 rounded-2xl border border-sun bg-sun-light px-4 py-3 text-sm font-semibold text-sun-dark transition hover:brightness-95"
        >
          <Clock size={16} />
          {t("pendingBanner", { n: pendingCount })}
          <ChevronRight size={16} className="ml-auto" />
        </Link>
      ) : null}

      {/* detail modal with bottom actions */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        title={t("detailTitle")}
        size="sm"
        footer={
          selected?.committed ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirmNoShow(true)}
                className="mr-auto text-danger"
              >
                <TriangleAlert size={16} />
                {t("reportNoShow")}
              </Button>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                {t("close")}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setSelected(null)}>
              {t("close")}
            </Button>
          )
        }
      >
        {selected ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Avatar
                name={selected.workerFullName ?? selected.workerMaskedName}
                src={selected.workerAvatarUrl ?? undefined}
                size="md"
                tone="lime"
              />
              <div className="min-w-0 flex-1">
                <p className="wrap-break-word font-bold text-ink">
                  {selected.workerFullName ?? selected.workerMaskedName}
                </p>
                <p className="truncate text-xs text-ash">
                  {selected.postingTitle}
                </p>
              </div>
              <Badge tone={selected.committed ? "lime-soft" : "sun-soft"}>
                {selected.committed ? t("confirmed") : t("editable")}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-sm text-ink/80">
              <Clock size={15} className="text-ash" />
              {
                WD[
                  new Date(selected.startAt).getDay() === 0
                    ? 6
                    : new Date(selected.startAt).getDay() - 1
                ]
              }{" "}
              {ddmm(new Date(selected.startAt))} ·{" "}
              {hhmm(new Date(selected.startAt))}
              {" – "}
              {hhmm(new Date(selected.endAt))}
            </div>
            <div className="flex items-center gap-2 text-sm text-ink/80">
              <MapPin size={15} className="text-ash" />
              {selected.orgAddressAlias} · {selected.district}
            </div>

            {selected.committed && selected.contact ? (
              <div className="mt-1 flex flex-col gap-2 border-t border-smoke pt-3">
                {selected.contact.contactPhone ? (
                  <a
                    href={`tel:${selected.contact.contactPhone}`}
                    className="flex items-center gap-2.5 rounded-xl bg-mist/50 px-3 py-2 transition hover:bg-mist"
                  >
                    <Phone size={15} className="shrink-0 text-ash" />
                    <span className="flex-1 text-sm font-semibold tabular-nums text-ink">
                      {selected.contact.contactPhone}
                    </span>
                  </a>
                ) : null}
                {selected.contact.email ? (
                  <a
                    href={`mailto:${selected.contact.email}`}
                    className="flex items-center gap-2.5 rounded-xl bg-mist/50 px-3 py-2 transition hover:bg-mist"
                  >
                    <Mail size={15} className="shrink-0 text-ash" />
                    <span className="flex-1 break-all text-sm font-semibold text-ink">
                      {selected.contact.email}
                    </span>
                  </a>
                ) : null}
                {selected.contact.zalo ? (
                  <a
                    href={`https://zalo.me/${selected.contact.zalo.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2.5 rounded-xl bg-lime-light px-3 py-2 transition hover:brightness-95"
                  >
                    <MessageCircle
                      size={15}
                      className="shrink-0 text-lime-dark"
                    />
                    <span className="flex-1 text-sm font-semibold text-ink">
                      Nhắn Zalo
                    </span>
                  </a>
                ) : null}
              </div>
            ) : (
              <p className="rounded-xl bg-mist/60 px-3 py-2 text-xs text-ash">
                {t("contactLocked")}
              </p>
            )}

            <button
              type="button"
              onClick={() => setProfileAppId(selected.applicationId)}
              className="mt-1 flex w-full items-center gap-2.5 rounded-xl border border-smoke px-3 py-2.5 text-left transition hover:bg-mist"
            >
              <FileText size={16} className="shrink-0 text-ash" />
              <span className="flex-1 text-sm font-semibold text-ink">
                {t("viewFullProfile")}
              </span>
              <ChevronRight size={16} className="shrink-0 text-ash/60" />
            </button>
          </div>
        ) : null}
      </Dialog>

      {profileAppId ? (
        <WorkerProfileModal
          applicationId={profileAppId}
          open
          readOnly
          onClose={() => setProfileAppId(null)}
        />
      ) : null}

      <ConfirmDialog
        open={confirmNoShow}
        onClose={() => setConfirmNoShow(false)}
        onConfirm={() => {
          if (!selected) return;
          noShow.mutate(selected.inviteId, {
            onSuccess: () => {
              toast({ tone: "success", title: t("noShowDone") });
              setConfirmNoShow(false);
              setSelected(null);
            },
            onError: () => toast({ tone: "error", title: t("noShowError") }),
          });
        }}
        title={t("noShowTitle")}
        description={t("noShowDesc")}
        confirmLabel={t("reportNoShow")}
        destructive
        loading={noShow.isPending}
      />
    </div>
  );
}
